import React from 'react';

const Terms = () => {
  const styles = {
    page: { background: '#f8fafc', minHeight: '100vh', padding: '48px 20px', fontFamily: '"Inter", Arial, sans-serif' },
    card: { maxWidth: '820px', margin: '0 auto', background: '#ffffff', borderRadius: '16px', padding: '48px', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', color: '#1e293b', lineHeight: '1.7' },
    h1: { fontSize: '32px', fontWeight: '800', marginBottom: '8px', color: '#0f172a' },
    meta: { color: '#64748b', fontSize: '14px', marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #e2e8f0' },
    h2: { fontSize: '20px', fontWeight: '700', marginTop: '36px', marginBottom: '12px', color: '#0f172a' },
    ul: { paddingLeft: '22px', margin: '12px 0' },
    li: { marginBottom: '8px' },
    callout: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px 24px', marginTop: '12px' },
    link: { color: '#059669', fontWeight: '600', textDecoration: 'none' },
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.h1}>Terms of Service</h1>
        <div style={styles.meta}>
          <strong>Tesla CRM Connect</strong>, a product of Tesla Online Solution<br />
          Effective date: 20 August 2026
        </div>

        <h2 style={styles.h2}>1. Acceptance of These Terms</h2>
        <p>
          These Terms of Service govern your access to and use of Tesla CRM Connect (the &ldquo;Service&rdquo;), operated by
          Tesla Online Solution (&ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating an account or using the Service, you agree to
          these terms. If you do not agree, do not use the Service.
        </p>

        <h2 style={styles.h2}>2. The Service</h2>
        <p>
          Tesla CRM Connect is a customer relationship and messaging platform that lets businesses manage conversations
          with their customers over WhatsApp. It provides a shared inbox, contact management, message templates,
          broadcasts, drip campaigns, and automated replies, built on Meta&rsquo;s WhatsApp Business Platform.
        </p>

        <h2 style={styles.h2}>3. Your Account</h2>
        <ul style={styles.ul}>
          <li style={styles.li}>You must provide accurate registration information and keep it up to date.</li>
          <li style={styles.li}>You are responsible for all activity that occurs under your account.</li>
          <li style={styles.li}>You are responsible for keeping your login credentials and access tokens confidential.</li>
          <li style={styles.li}>You must have the authority to bind the business you register on behalf of.</li>
        </ul>

        <h2 style={styles.h2}>4. Customer Consent and Acceptable Use</h2>
        <p>
          You are solely responsible for the messages you send through the Service and for the contacts you upload. You
          agree that:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            You have obtained valid opt-in consent from every recipient before messaging them, as required by the
            WhatsApp Business Messaging Policy.
          </li>
          <li style={styles.li}>You will honour opt-out requests promptly and stop messaging contacts who ask you to.</li>
          <li style={styles.li}>You will not send spam, bulk unsolicited messages, or purchased contact lists.</li>
          <li style={styles.li}>
            You will not use the Service to send unlawful, fraudulent, misleading, harassing, or infringing content.
          </li>
          <li style={styles.li}>You will not attempt to disrupt, reverse engineer, or gain unauthorised access to the Service.</li>
        </ul>
        <p>
          Violating these rules may result in suspension of your account, and may also cause Meta to restrict or ban your
          WhatsApp Business account, which is outside our control.
        </p>

        <h2 style={styles.h2}>5. Meta and WhatsApp Terms</h2>
        <p>
          The Service depends on Meta&rsquo;s WhatsApp Business Platform. Your use of WhatsApp messaging is additionally
          governed by Meta&rsquo;s terms and policies, and you agree to comply with them. Message pricing, rate limits,
          template approval, and account quality ratings are determined by Meta, not by us, and may change at any time.
        </p>

        <h2 style={styles.h2}>6. Fees and Billing</h2>
        <p>
          Certain features are billed on a prepaid wallet basis. Messages sent through the Service are charged against your
          wallet balance at the rates shown in your dashboard, which reflect Meta&rsquo;s conversation pricing plus any
          platform fee. Wallet recharges are processed by our payment provider. Unless required by law, amounts already
          consumed for delivered messages are non-refundable.
        </p>

        <h2 style={styles.h2}>7. Your Data</h2>
        <p>
          You retain ownership of your business data and your customers&rsquo; data. We process it only to operate the
          Service, as described in our{' '}
          <a href="/privacy-policy" style={styles.link}>Privacy Policy</a>. You can request deletion at any time through
          our <a href="/data-deletion" style={styles.link}>Data Deletion</a> page.
        </p>

        <h2 style={styles.h2}>8. Service Availability</h2>
        <p>
          We aim to keep the Service running reliably, but we do not guarantee uninterrupted or error-free operation. The
          Service may be unavailable during maintenance, or because of failures in third-party services we depend on,
          including Meta&rsquo;s platform and our cloud infrastructure providers.
        </p>

        <h2 style={styles.h2}>9. Disclaimer and Limitation of Liability</h2>
        <p>
          The Service is provided &ldquo;as is&rdquo; without warranties of any kind, express or implied. To the maximum
          extent permitted by law, we are not liable for indirect, incidental, or consequential damages, for lost profits,
          or for loss of data or business arising from your use of the Service. Our total liability for any claim relating
          to the Service is limited to the amount you paid us in the three months preceding the claim.
        </p>

        <h2 style={styles.h2}>10. Suspension and Termination</h2>
        <p>
          You may stop using the Service and close your account at any time. We may suspend or terminate your account if
          you breach these terms, if your use puts the platform or other users at risk, or if required by Meta or by law.
          On termination, your data is handled as described in our Privacy Policy and Data Deletion page.
        </p>

        <h2 style={styles.h2}>11. Changes to These Terms</h2>
        <p>
          We may update these terms from time to time. Changes will be posted on this page with a revised effective date.
          Continuing to use the Service after changes take effect means you accept the updated terms.
        </p>

        <h2 style={styles.h2}>12. Governing Law</h2>
        <p>
          These terms are governed by the laws of India, and the courts of India will have jurisdiction over any dispute
          arising from them.
        </p>

        <h2 style={styles.h2}>13. Contact Us</h2>
        <p>If you have questions about these terms, contact us at:</p>
        <div style={styles.callout}>
          <strong>Tesla Online Solution</strong><br />
          Email: <a href="mailto:teslaonlinesolution@gmail.com" style={styles.link}>teslaonlinesolution@gmail.com</a>
        </div>
      </div>
    </div>
  );
};

export default Terms;
